import {
  EntityModelLayer,
  EntityPropertyLayer,
  ComputedLayer,
  layerType,
} from "@/models/Layers.model";
import { ActiveCategory } from "@/models/Categorical.model";
import {
  getConsoleMeta,
  getRawConsoleData,
  getPublicConsoleData,
} from "@/api/console";
import { types } from "./mutations";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import router from "@/routes";
import notify from "@/lib/notifications";

export default {
  /**
   * Initializes the studio into edit mode
   */
  initializeEditor: async (
    { state, commit, dispatch, rootGetters },
    { dashboard_id = null }
  ) => {
    // if dashboard id is not present, then initialize an empty editor
    if (!dashboard_id) {
      commit(types.INITIALIZE_EDITOR);
    } else {
      // if dashboard id is present, initialize the editor after fetching the dashboard data
      let response = null;

      try {
        response = await getRawConsoleData(dashboard_id);
        const { status, data } = response;

        if (status !== 200 || !data) {
          throw new Error("Unable to open this console");
        }
      } catch (error) {
        // if unable to open the console, fallback!
        notify.error("Unable to open this console");
        router.push("/");
      }

      const entities = await dispatch("Entities/fetchEntities", null, {
        root: true,
      });

      dispatch("Dashboards/fetchConsoles", null, {
        root: true,
      });

      await dispatch("Datasources/fetchDataSources", null, {
        root: true,
      });

      const { data } = response.data;
      commit(types.SET_META_DATA, {
        id: dashboard_id,
        name: data.name,
        description: data.description,
        city: data.city,
        created_by: data.created_by,
      });
      const { metric_ids, metrics, cohorts, meta } = data;
      const entityModels = [];
      const entityProps = [];
      const computedLayers = [];
      const appliedFilters = [];
      const selectedFilters = [];
      const lockedFilters = [];
      const layerRegistry = [];

      // check if the list contains details of all the metrics
      if (metric_ids && metric_ids.length > 0) {
        cohorts.forEach(cohort => {
          // get the details required for the cohort from it's metrics
          // TODO: Shouldn't we be looping over the cohort's metric arrays?
          // HELP: One Cohort can have multiple metrics, so we should be interating..
          if (cohort.metrics && cohort.metrics.length > 0) {
            let firstMetric = cohort.metrics[0];
            cohort.entity_id = firstMetric.entity_id;
            cohort.entity_name = "";
            // get all the applied filter and locked filters
            firstMetric.expr_terms[0].entity_filters.forEach(item => {
              // push to locked layers
              lockedFilters.push({
                id: item.colname,
                entity_model_id: cohort.id,
              });

              // push to applied filters
              appliedFilters.push(
                new ActiveCategory({
                  name: "",
                  category_id: item.colname,
                  comparison: item.comparison,
                  item_id: item.value,
                  entity_model_id: cohort.id,
                })
              );
            });

            // get all the selected filters
            firstMetric.selected_filters.forEach(item => {
              selectedFilters.push({
                id: item,
                entity_model_id: cohort.id,
              });
            });
          } else {
            router.push("/");
            return null;
          }

          // loop through each of the cohort metrics and add it to the entityprops
          cohort.metrics.forEach(metric => {
            let columns = [];
            let selectedEntity = entities.find(
              entity => entity.id === metric.entity_id
            );

            // Figure out options for property and aggregation in Entityproplayer:
            if (selectedEntity) {
              // get all the datasource details of the selected entity
              selectedEntity.datasources.map(datasource => {
                // get all the selected columns of the entity from each of its datasource

                let dataSourceDetails = rootGetters[
                  "Datasources/getDataSourceDetails"
                ](datasource.datasource_id);

                if (dataSourceDetails && dataSourceDetails.columns) {
                  dataSourceDetails.columns.forEach(column => {
                    // add the columns only if it's a measure
                    if (
                      datasource.selected_cols.includes(column.name) &&
                      column.is_measure
                    ) {
                      columns.push(column);
                    }
                    // check if the column is an entity id col, then push that
                    else if (
                      datasource.type === "base" &&
                      column.name === selectedEntity.entity_id_col
                    ) {
                      columns.push({
                        ...column,
                        aggs: [
                          {
                            id: "count_unique",
                            name: "count_unique",
                          },
                          {
                            id: "count",
                            name: "count",
                          },
                        ],
                      });
                    }
                  });
                }
              });
            }

            entityProps.push(
              new EntityPropertyLayer({
                id: metric.id,
                name: metric.name,
                entity_model_id: cohort.id,
                property: {
                  id: metric.expr_terms[0].agg.prop, // "raw_7e41fa6d17664dc7871873b0aa6fd87d__user_id"
                  name: metric.expr_terms[0].agg.prop.split("__")[1], // "user_id"
                  aggs: columns.find(
                    col =>
                      col.name === metric.expr_terms[0].agg.prop.split("__")[1]
                  ).aggs,
                },
                aggregation: {
                  id: metric.expr_terms[0].agg.func, // "count_unique"
                  name: metric.expr_terms[0].agg.func, // "count_unique"
                },
                value: 0,
                isEditable: true,
              })
            );
          });

          const entityModel = new EntityModelLayer({
            id: cohort.id,
            name: cohort.name,
            entity_id: cohort.entity_id,
            entity_name: "",
            entity_type: cohort.metrics[0].expr_terms[0].entity_type,
            isEditable: true,
          });
          layerRegistry.push({
            id: entityModel.id,
            type: layerType.ENTITY_MODEL,
          });
          entityModels.push(entityModel);
        });

        // Traversing Metrics (Computed Layers):

        metrics.forEach(metric => {
          layerRegistry.push({
            id: metric.id,
            type: layerType.COMPUTED_PROP,
          });

          // Finding the matching metrics for each expr_term of a metric (Computed layer):
          let matchedMetrics = metric.expr_terms.map(exp_term => {
            // Iterate over each exp_term of each metric of each cohort, to find the exp_term which matches the exp_term of metrics (computed layer)
            for (let cohort of cohorts) {
              for (let C_metric of cohort.metrics) {
                for (let CM_exp_term of C_metric.expr_terms) {
                  if (
                    isEqual(omit(CM_exp_term, "name"), omit(exp_term, "name"))
                  ) {
                    return C_metric;
                  }
                }
              }
            }
            // Techinally this should never happen, but if it happens we will see what to do:
            console.log("ERROR: Cannot match a metric for the Computed layer");
            return "Unmatched";
          });

          computedLayers.push(
            new ComputedLayer({
              id: metric.id,
              name: metric.name,
              description: matchedMetrics
                .map(matchedMetric => matchedMetric.name)
                .join("-"),
              variables: matchedMetrics.map(matchedMetric => matchedMetric.id),
              expression: metric.expr_str,
              value: 0,
              isEditable: true,
            })
          );
        });

        // If meta is there and it has a non empty city object, then set the activeCity:
        if (
          meta &&
          Object.entries(meta.city).length !== 0 &&
          meta.city.constructor === Object
        ) {
          await dispatch("Map/setActiveCity", meta.city, { root: true });
        }

        let geo_level = state.gridLevels[3];
        // If meta is there and it has grid level, then set activeGridLevel:
        if (meta && meta.grid_level >= 0) {
          await dispatch("StudioManager/setActiveGridLevel", meta.grid_level, {
            root: true,
          });
          geo_level = state.gridLevels[meta.grid_level];
        }

        await dispatch(
          "LayerManager/loadInitialData",
          {
            entityModels,
            entityProps,
            computedLayers,
            layerRegistry,
          },
          { root: true }
        );

        await dispatch(
          "FilterManager/loadInitialData",
          {
            selectedFilters,
            appliedFilters,
            lockedFilters,
          },
          { root: true }
        );

        const payload = await dispatch(
          "LayerManager/createPayload",
          {
            active: false,
            event: "time_window",
          },
          { root: true }
        );

        const categorical = await dispatch(
          "FilterManager/createEntityFilterPayload",
          null,
          { root: true }
        );
        if (categorical && categorical.metric) {
          payload.push(categorical);
        }

        dispatch(
          "DataManager/fetchWidgets",
          { payload, filters: { geo_level }, temp: true },
          { root: true }
        );
      }
      commit(types.INITIALIZE_EDITOR);
      return true;
    }
  },

  /**
   * Initialized the studio in view mode
   * Loads the data for the specified dashboard_id and passes it along to all the other managers
   */
  initializeViewer: async (
    { state, commit, dispatch },
    { dashboard_id = null, magic_link = null }
  ) => {
    let response = null;
    let isPublicView = false;
    try {
      if (dashboard_id) {
        response = await getConsoleMeta(dashboard_id);
      } else {
        response = await getPublicConsoleData(magic_link);
        isPublicView = true;
      }

      if (!(response && response.data && response.data.data)) {
        throw new Error("No dashboard data found");
      }
    } catch (error) {
      if (isPublicView) {
        router.push("/404");
      } else {
        router.push("/");
      }
      return null;
    }

    const { data } = response.data;
    // set the metadata of the console
    commit(types.SET_META_DATA, {
      id: dashboard_id,
      name: data.name,
      description: data.description,
      city: data.city,
      created_by: data.created_by,
    });

    const { metric_ids, metrics, cohorts, meta } = data;
    const entityModels = [];
    const entityProps = [];
    const computedLayers = [];
    const appliedFilters = [];
    const selectedFilters = [];
    const lockedFilters = [];
    const layerRegistry = [];

    // check if the list contains details of all the metrics
    if (metric_ids && metric_ids.length > 0) {
      cohorts.forEach(cohort => {
        // get the details required for the cohort from it's metrics
        if (cohort.metrics && cohort.metrics.length > 0) {
          let firstMetric = cohort.metrics[0];
          cohort.entity_id = firstMetric.entity_id;
          cohort.entity_name = "";
          // get all the applied filter and locked filters
          firstMetric.entity_filters.forEach(item => {
            // push to locked layers
            lockedFilters.push({
              id: item.colname,
              entity_model_id: cohort.id,
            });

            // push to applied filters
            appliedFilters.push(
              new ActiveCategory({
                name: "",
                category_id: item.colname,
                comparison: item.comparison,
                item_id: item.value,
                entity_model_id: cohort.id,
              })
            );
          });

          // get all the selected filters
          firstMetric.selected_filters.forEach(item => {
            selectedFilters.push({
              id: item,
              entity_model_id: cohort.id,
            });
          });
        } else {
          router.push("/");
          return null;
        }

        // loop through each of the cohort metrics and add it to the entityprops
        cohort.metrics.forEach(metric => {
          entityProps.push(
            new EntityPropertyLayer({
              id: metric.id,
              name: metric.name,
              entity_model_id: cohort.id,
              value: 0,
              isEditable: false,
            })
          );
        });
        const entityModel = new EntityModelLayer({
          id: cohort.id,
          name: cohort.name,
          entity_id: cohort.entity_id,
          entity_name: "",
          isEditable: false,
        });
        layerRegistry.push({
          id: entityModel.id,
          type: layerType.ENTITY_MODEL,
        });
        entityModels.push(entityModel);
      });

      metrics.forEach(metric => {
        layerRegistry.push({
          id: metric.id,
          type: layerType.COMPUTED_PROP,
        });

        computedLayers.push(
          new ComputedLayer({
            id: metric.id,
            name: metric.name,
            value: 0,
            isEditable: false,
          })
        );
      });

      // If meta is there and it has a non empty city object, then set the activeCity:
      if (
        meta &&
        meta.city &&
        Object.entries(meta.city).length !== 0 &&
        meta.city.constructor === Object
      ) {
        await dispatch("Map/setActiveCity", meta.city, { root: true });
      }

      let geo_level = state.gridLevels[3];
      // If meta is there and it has grid level, then set activeGridLevel:
      if (meta && meta.grid_level >= 0) {
        await dispatch("StudioManager/setActiveGridLevel", meta.grid_level, {
          root: true,
        });
        geo_level = state.gridLevels[meta.grid_level];
      }

      await dispatch(
        "LayerManager/loadInitialData",
        {
          entityModels,
          entityProps,
          computedLayers,
          layerRegistry,
        },
        { root: true }
      );

      await dispatch(
        "FilterManager/loadInitialData",
        {
          selectedFilters,
          appliedFilters,
          lockedFilters,
        },
        { root: true }
      );

      const payload = await dispatch(
        "LayerManager/createPayload",
        {
          active: false,
          event: "time_window",
        },
        { root: true }
      );

      const categorical = await dispatch(
        "FilterManager/createEntityFilterPayload",
        null,
        { root: true }
      );
      if (categorical && categorical.metric) {
        payload.push(categorical);
      }

      dispatch(
        "DataManager/fetchWidgets",
        { payload, filters: { geo_level }, temp: false, public: true },
        { root: true }
      );
    }
    if (isPublicView) {
      commit(types.INITIALIZE_PUBLIC_VIEW);
    } else {
      commit(types.INITIALIZE_VIEWER);
    }
    return true;
  },

  /**
   * Toggles the filters view
   */
  toggleFilterManager: async ({ commit }) => {
    commit(types.TOGGLE_FILTER_MANAGER);
  },

  /**
   * Sets the active grid level
   */
  setActiveGridLevel: async ({ commit }, value) => {
    commit(types.SET_ACTIVE_GRID_LEVEL, value);
  },

  /**
   * Resets the studio and related modules
   */
  reset: async ({ commit, dispatch }) => {
    dispatch("LayerManager/reset", null, { root: true });
    dispatch("FilterManager/reset", null, { root: true });
    dispatch("DataManager/reset", null, { root: true });
    dispatch("TimeManager/reset", null, { root: true });
    dispatch("Map/reset", null, { root: true });
    dispatch("Popup/reset", null, { root: true });
    commit(types.reset);
  },
};
